/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */

/**
 *
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.3.0
 * @since 1.1.0
 */

import { ReactElement, ReactNode, RefObject, useEffect, useRef } from 'react';

import InfiniteScroll from 'react-infinite-scroller';

import { FlexStyled } from '../Block/Flexbox';
import SideBarNav from './SideBarNav';
import { SideBarContentContainerStyled, SideBarNavContainerStyled } from './styled/SideBarStyled';

export interface ISideBarProps {
    children: [ReactElement<typeof SideBarNav>, ReactNode];
    hasMore: boolean;
    loadMore?: (page: number) => void;
    minHeight: number;
    navMarginBottom: number;
    navWidth: number;
}

const SideBar = (props: ISideBarProps) => {

    const navRef: RefObject<HTMLDivElement> = useRef(null);

    const { navMarginBottom, navWidth } = props;

    const scrollHandler = (): void => {
        if (navRef.current) {
            navRef.current.style.height =
                `calc(100vh - ${navRef.current.getBoundingClientRect().top + navMarginBottom}px)`;
        }
    };

    const loadMore = (page: number): void => {
        if (props.loadMore) {
            props.loadMore(page);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', scrollHandler, true);
        scrollHandler();

        return () => {
            window.removeEventListener('scroll', scrollHandler, true);
        };
    }, [navMarginBottom, navRef.current?.getBoundingClientRect().top]);

    return (
        <FlexStyled>
            {
                navWidth > 0 &&
                <SideBarNavContainerStyled
                    marginBottom={navMarginBottom}
                    ref={navRef}
                    width={navWidth}
                >
                    <InfiniteScroll
                        getScrollParent={() => navRef.current}
                        hasMore={props.hasMore}
                        initialLoad={false}
                        loadMore={loadMore}
                        pageStart={0}
                        threshold={100}
                        useWindow={false}
                    >
                        {props.children[0]}
                    </InfiniteScroll>
                </SideBarNavContainerStyled>
            }
            <SideBarContentContainerStyled minHeight={props.minHeight} navWidth={navWidth}>
                {props.children[1]}
            </SideBarContentContainerStyled>
        </FlexStyled>
    );
};

SideBar.defaultProps = {
    hasMore: false,
    minHeight: 500,
    navMarginBottom: 32,
    navWidth: 200,
};

export default SideBar;
